import { colors } from "./colors";
import { down } from "./breakpoints";

/**
 * Styles shared by every page rendered inside <Sidebar>.
 *
 * Each page used to repeat the same block with slightly different magic
 * numbers:
 *
 *   contentMain:  { overflow: "hidden", height: "100vh" }
 *   vehiculeRoot: { height: "calc(100vh - 60px)", padding: "30px 30px", ... }
 *
 * The `- 60px` was the page's own vertical padding subtracted by hand. It broke
 * whenever the padding changed, and it left no room for a mobile header. The
 * sidebar shell now sizes the content area with flexbox instead, so pages only
 * need to declare padding and background.
 */

/**
 * MUI's TablePagination toolbar lays out on a single non-wrapping row:
 * "Lignes par page" + selector + "1-10 sur 42" + two arrows. That needs roughly
 * 420px, so on a phone it pushed the table wider than the screen and dragged a
 * horizontal scrollbar onto every list in the app.
 *
 * Dropping the label (the selector beside it is self-evident) and letting the
 * toolbar wrap keeps the controls reachable. This lived only in Billing; every
 * other page with a table had the same overflow and no fix, so it is shared
 * from here now.
 *
 * Declared before the shells below because they spread it -- `const` is not
 * hoisted, so defining it after them would throw at module load.
 */
export const tablePaginationResponsive = {
  "& .MuiTablePagination-toolbar": {
    flexWrap: "wrap" as const,
    justifyContent: "flex-end",
    rowGap: "4px",
    [down.sm]: {
      paddingLeft: "8px",
    },
  },
  "& .MuiTablePagination-selectLabel": {
    [down.sm]: { display: "none" },
  },
  "& .MuiTablePagination-displayedRows": {
    margin: 0,
  },
};

/** Spread into the class passed as `sidebarRoot`. */
export const pageShellRoot = {
  overflow: "hidden",
  height: "var(--app-height)",
};

/**
 * Spread into the class passed as `sidebarRightRoot`.
 *
 * NOTE: this class is applied to the <main> element itself, not to a wrapper
 * inside it. Declare padding, background and layout here - but do NOT declare
 * `width` or `maxWidth`. The shell offsets the content against the sidebar
 * with its own `padding-left`, and a width declared here would be measured
 * against the shell's padded content box for no benefit, while re-opening the
 * class of bug where a page rendered wider than the area it was given and had
 * its right edge clipped away by the shell's `overflow: hidden`.
 *
 * Padding tightens on small screens, where 30px on each side wastes a
 * meaningful share of the viewport.
 */
export const pageContentRoot = {
  display: "flex",
  flexDirection: "column" as const,
  gap: "15px",
  padding: "30px",
  backgroundColor: colors.bgcolor,
  [down.md]: {
    padding: "20px 16px",
  },
  [down.sm]: {
    padding: "16px 12px",
    gap: "12px",
  },
  ...tablePaginationResponsive,
};

/**
 * A page header row: title on the left, actions on the right. Wraps to two
 * lines rather than letting the action button overflow off-screen.
 */
export const pageHeaderRow = {
  display: "flex",
  flexDirection: "row" as const,
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap" as const,
  gap: "12px",
  width: "100%",
  [down.sm]: {
    alignItems: "flex-start",
    flexDirection: "column" as const,
  },
};

/**
 * A responsive card row: equal-width columns that wrap onto new lines instead
 * of squeezing below a readable width.
 */
export const cardGrid = (minColumnWidth = 240) => ({
  display: "grid",
  gridTemplateColumns: `repeat(auto-fit, minmax(min(${minColumnWidth}px, 100%), 1fr))`,
  gap: "12px",
});

/**
 * The wider page shell used by the management screens (Billing, Brands, Pub,
 * Parametre).
 *
 * These four each carried a byte-identical copy of this padding ladder, and
 * all four carried the same `width: 100%` fault, because the block was
 * copy-pasted rather than shared. One CSS mistake therefore shipped four
 * times. Same contract as `pageContentRoot`: no `width`, no `maxWidth`.
 */
export const pageContentRootWide = {
  minWidth: 0,
  boxSizing: "border-box" as const,
  backgroundColor: colors.bgcolor,
  padding: "24px 32px 32px 32px",
  [down.lg]: {
    padding: "20px 24px 24px 24px",
  },
  [down.md]: {
    padding: "16px 20px 20px 20px",
  },
  [down.sm]: {
    padding: "12px 16px 16px 16px",
  },
  ...tablePaginationResponsive,
};

/** Horizontal gutter of `pageContentRootWide` at the `sm` breakpoint. */
export const PAGE_GUTTER_SM = 16;

/**
 * Cancels the page gutter so a wide table gets the full screen width to scroll
 * within on a phone.
 *
 * The negative margins MUST match the page's own horizontal padding. Hand-
 * written copies had drifted to 12px against a 16px gutter, leaving the card
 * sitting 4px proud of the content on each side; deriving both from one
 * constant makes that impossible.
 */
export const fullBleedOnMobile = {
  marginLeft: `-${PAGE_GUTTER_SM}px`,
  marginRight: `-${PAGE_GUTTER_SM}px`,
  width: `calc(100% + ${PAGE_GUTTER_SM * 2}px)`,
  borderRadius: 0,
  borderLeft: "none",
  borderRight: "none",
};
